import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { formatCurrency, formatDateTime, STATUS_LABELS, STATUS_COLORS } from '../lib/utils';
import { 
  FileText, 
  Clock, 
  Mail, 
  CheckCircle, 
  XCircle, 
  PlusCircle,
  TrendingUp,
  Wallet,
  ArrowRight,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingFlyer, setDownloadingFlyer] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        api.getDashboardStats(),
        api.getOrders()
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const downloadProductFlyer = async () => {
    setDownloadingFlyer(true);
    try {
      const response = await api.downloadProductFlyer();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nexiu-Produktflyer-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Produktflyer heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen des Flyers');
    } finally {
      setDownloadingFlyer(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F47920]"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Gesamt Bestellungen',
      value: stats?.total_orders || 0,
      icon: FileText,
      color: 'bg-slate-100 text-slate-600'
    },
    {
      title: 'Ausstehend',
      value: stats?.pending_orders || 0,
      icon: Clock,
      color: 'bg-amber-100 text-amber-600'
    },
    {
      title: 'Versendet',
      value: stats?.sent_orders || 0,
      icon: Mail,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Bestätigt',
      value: stats?.confirmed_orders || 0,
      icon: CheckCircle,
      color: 'bg-emerald-100 text-emerald-600'
    }
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Willkommen, {user?.first_name}!
          </h1>
          <p className="text-slate-600 mt-1">
            Hier ist Ihre Übersicht für heute
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={downloadProductFlyer}
            variant="outline"
            className="border-[#F47920] text-[#F47920] hover:bg-[#F47920]/10 gap-2"
            disabled={downloadingFlyer}
            data-testid="download-flyer-btn"
          >
            <Download className="w-4 h-4" />
            {downloadingFlyer ? 'Lädt...' : 'Produktflyer'}
          </Button>
          <Button 
            onClick={() => navigate('/new-order')}
            className="bg-[#F47920] hover:bg-[#E06810] gap-2"
            data-testid="new-order-btn"
          >
            <PlusCircle className="w-4 h-4" />
            Neue Bestellung
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Card key={stat.title} className="card-hover border-0 shadow-sm" data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Commission card for sales users */}
      {user?.role === 'sales' && stats?.commission_total > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-[#F47920] to-[#E06810] text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm font-medium">Ihre Provisionen</p>
                <p className="text-4xl font-bold mt-2">{formatCurrency(stats.commission_total)}</p>
                <p className="text-white/70 text-sm mt-1">
                  aus {stats.confirmed_orders} bestätigten Bestellungen
                </p>
              </div>
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                <Wallet className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Orders */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Letzte Bestellungen
          </CardTitle>
          <Link to="/orders">
            <Button variant="ghost" className="gap-2 text-[#F47920]" data-testid="view-all-orders">
              Alle anzeigen
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentOrders.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500">Noch keine Bestellungen vorhanden</p>
              <Button 
                onClick={() => navigate('/new-order')} 
                className="mt-4 bg-[#F47920] hover:bg-[#E06810]"
              >
                Erste Bestellung erstellen
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentOrders.map((order) => (
                <Link 
                  key={order.id} 
                  to={`/orders/${order.id}`}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                  data-testid={`order-${order.order_number}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#F47920]/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#F47920]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">#{order.order_number}</p>
                      <p className="text-sm text-slate-500">
                        {order.customer.first_name} {order.customer.last_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <p className="font-medium text-slate-900">{formatCurrency(order.monthly_total)}/Monat</p>
                      <p className="text-sm text-slate-500">{order.product_name}</p>
                    </div>
                    <Badge className={STATUS_COLORS[order.status]}>
                      {STATUS_LABELS[order.status]}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
